import { Currency } from "@redotech/money/currencies";
import { ServiceSchema } from "@redotech/redo-fulfillment-carrier-common/carrier-service-catalog";
import {
  Carrier,
  CarrierSchema,
  getTrackingLink,
  getTrackingLinkCarrier,
} from "@redotech/redo-fulfillment-carrier-common/carriers-and-services";
import { KnownSCAC } from "@redotech/redo-fulfillment-carrier-common/freight";
import { surchargeSchema_ } from "@redotech/redo-fulfillment-outbound-labels-common/surcharge";
import {
  CurrencySchema,
  MoneySchema,
} from "@redotech/redo-fulfillment-outbound-labels-common/util";
import {
  getBillingAccountId,
  isExternalAccountBilling,
  RateProvider,
} from "@redotech/redo-fulfillment-rate-common/outbound-labels";
import { privacy } from "@redotech/schema-privacy";
import { DateFromDateOrIsoStringSchema } from "@redotech/zod-util/date-schema";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import {
  ShipmentPackage,
  ShipmentTrackerStatus,
} from "./outbound-shipment-label";

const shipmentPackageSummarySchema = z
  .object({
    trackingCode: z.string().meta({ privacy: privacy.id.tracking }).nullish(),
    trackingCarrier: CarrierSchema.nullish().meta({
      privacy: privacy.structured,
    }),
    trackingUrl: z.string().meta({ privacy: privacy.publicUrl }).nullish(),
    trackingStatus: createEnum(ShipmentTrackerStatus, {
      enumName: "ShipmentTrackerStatus",
    })
      .nullish()
      .meta({ privacy: privacy.enumOrStatus }),
    estDeliveryDate: DateFromDateOrIsoStringSchema.meta({
      privacy: privacy.timestamp,
    }).nullish(),
    deliveredAt: DateFromDateOrIsoStringSchema.meta({
      privacy: privacy.timestamp,
    }).nullish(),
    firstScanAt: DateFromDateOrIsoStringSchema.meta({
      privacy: privacy.timestamp,
    }).nullish(),
  })
  .meta({ privacy: privacy.structured });
export const ShipmentPackageSummarySchema: z.ZodType<ShipmentPackageSummary> =
  shipmentPackageSummarySchema;
export interface ShipmentPackageSummary extends z.infer<
  typeof shipmentPackageSummarySchema
> {}

/** Mirrors the outbound-shipments CarrierShipment: one buy per carrier
 * shipment, which may cover many packages (UPS multi-parcel) or just one
 * (USPS multi-parcel is many carrier shipments). */
const carrierShipmentSummarySchema = z
  .object({
    purchased: z.boolean().meta({ privacy: privacy.flag }),
    carrier: CarrierSchema.nullish().meta({ privacy: privacy.structured }),
    service: ServiceSchema.nullish().meta({ privacy: privacy.structured }),
    purchasedAt: DateFromDateOrIsoStringSchema.meta({
      privacy: privacy.timestamp,
    }).nullish(),
    provider: createEnum(RateProvider, { enumName: "RateProvider" })
      .nullish()
      .meta({ privacy: privacy.enumOrStatus }),
    rateVersion: z.number().meta({ privacy: privacy.enumOrStatus }).nullish(),
    simpleRateCode: z
      .string()
      .meta({ privacy: privacy.enumOrStatus })
      .nullish(),
    isMerchantCarrierAccount: z
      .boolean()
      .meta({ privacy: privacy.flag })
      .nullish(),
    isExternalBilling: z.boolean().meta({ privacy: privacy.flag }),
    billingAccountId: z
      .string()
      .meta({ privacy: privacy.id.external.resource })
      .nullish(),
    cost: MoneySchema.nullish().meta({ privacy: privacy.structured }),
    voided: z.boolean().meta({ privacy: privacy.flag }),
    hasLabel: z.boolean().meta({ privacy: privacy.flag }),
    commercialInvoiceUrl: z
      .string()
      .meta({ privacy: privacy.privateUrl })
      .nullish(),
    comInvoiceSubmittedElectronically: z
      .boolean()
      .meta({ privacy: privacy.flag })
      .nullish(),
    createdAt: DateFromDateOrIsoStringSchema.meta({
      privacy: privacy.timestamp,
    }).nullish(),
    carrierAdjustments: z
      .array(
        surchargeSchema_.and(
          z.object({
            balanceTransactionId: z
              .string()
              .meta({ privacy: privacy.id.external.resource }),
          }),
        ),
      )
      .nullish()
      .meta({ privacy: privacy.structured }),
    packages: z
      .array(ShipmentPackageSummarySchema.meta({ privacy: privacy.structured }))
      .meta({ privacy: privacy.structured }),
  })
  .meta({ privacy: privacy.structured });
export const CarrierShipmentSummarySchema: z.ZodType<CarrierShipmentSummary> =
  carrierShipmentSummarySchema;
export interface CarrierShipmentSummary extends z.infer<
  typeof carrierShipmentSummarySchema
> {}

const shipmentSummaryBaseSchema = z
  .object({
    carrierShipments: z
      .array(CarrierShipmentSummarySchema.meta({ privacy: privacy.structured }))
      .meta({ privacy: privacy.structured }),
    totalCost: MoneySchema.nullish().meta({ privacy: privacy.structured }),
  })
  .meta({ privacy: privacy.structured });
export const ShipmentSummaryBaseSchema: z.ZodType<ShipmentSummaryBase> =
  shipmentSummaryBaseSchema;
export interface ShipmentSummaryBase extends z.infer<
  typeof shipmentSummaryBaseSchema
> {}

const freightSummarySchema = z
  .object({
    trackingCode: z.string().meta({ privacy: privacy.id.tracking }),
    trackingUrl: z.string().meta({ privacy: privacy.publicUrl }),
    scac: createEnum(KnownSCAC, { enumName: "KnownSCAC" }).meta({
      privacy: privacy.enumOrStatus,
    }),
    cost: MoneySchema.meta({ privacy: privacy.structured }),
    hasBol: z.boolean().meta({ privacy: privacy.flag }),
    canVoid: z.boolean().meta({ privacy: privacy.flag }),
  })
  .meta({ privacy: privacy.structured });
export const FreightSummarySchema: z.ZodType<FreightSummary> =
  freightSummarySchema;
export interface FreightSummary extends z.infer<typeof freightSummarySchema> {}

export enum UnvoidableReason {
  NO_LABEL = "noLabel",
  ALREADY_VOIDED = "alreadyVoided",
  GROUP_NOT_CLOSED = "groupNotClosed",
  ALREADY_SHIPPED = "alreadyShipped",
  PROVIDER_RESTRICTION = "providerRestriction",
}
const voidActionSummarySchema = z
  .discriminatedUnion("voidable", [
    z
      .object({ voidable: z.literal(true).meta({ privacy: privacy.flag }) })
      .meta({ privacy: privacy.structured }),
    z
      .object({
        voidable: z.literal(false).meta({ privacy: privacy.flag }),
        reason: createEnum(UnvoidableReason, {
          enumName: "UnvoidableReason",
        }).meta({ privacy: privacy.enumOrStatus }),
      })
      .meta({ privacy: privacy.structured }),
  ])
  .meta({ privacy: privacy.structured });
export const VoidActionSummarySchema: z.ZodType<VoidActionSummary> =
  voidActionSummarySchema;
export type VoidActionSummary = z.infer<typeof voidActionSummarySchema>;

export enum UnclaimableReason {
  IS_MERCHANT_ACCOUNT = "isMerchantAccount",
}
const claimActionSummarySchema = z
  .discriminatedUnion("claimable", [
    z
      .object({ claimable: z.literal(true).meta({ privacy: privacy.flag }) })
      .meta({ privacy: privacy.structured }),
    z
      .object({
        claimable: z.literal(false).meta({ privacy: privacy.flag }),
        reason: createEnum(UnclaimableReason, { enumName: "UnclaimableReason" })
          .nullish()
          .meta({ privacy: privacy.enumOrStatus }),
      })
      .meta({ privacy: privacy.structured }),
  ])
  .meta({ privacy: privacy.structured });
export const ClaimActionSummarySchema: z.ZodType<ClaimActionSummary> =
  claimActionSummarySchema;
export type ClaimActionSummary = z.infer<typeof claimActionSummarySchema>;

const shipmentSummarySchema = shipmentSummaryBaseSchema.extend({
  voidActionSummary: VoidActionSummarySchema,
  claimActionSummary: ClaimActionSummarySchema,
  freight: FreightSummarySchema.nullish(),
});
export const ShipmentSummarySchema: z.ZodType<ShipmentSummary> =
  shipmentSummarySchema;
export interface ShipmentSummary extends z.infer<
  typeof shipmentSummarySchema
> {}

function toCurrency(currency: string | null | undefined): Currency {
  const parsed = CurrencySchema.safeParse(currency);
  return parsed.success ? parsed.data : Currency.USD;
}

function toKnownCarrier(carrier: string | null | undefined): Carrier | null {
  const parsed = CarrierSchema.safeParse(carrier);
  return parsed.success ? parsed.data : null;
}

function buildPackageSummary(
  shipment: ShipmentPackage,
): ShipmentPackageSummary {
  const { tracker, boughtRate } = shipment;
  const trackingDetails = tracker?.trackingDetails ?? [];
  const deliveredAt = [...trackingDetails]
    .reverse()
    .find(
      (detail) => detail.status === ShipmentTrackerStatus.DELIVERED,
    )?.datetime;
  const firstScanAt = trackingDetails
    .filter((detail) => detail.status === ShipmentTrackerStatus.IN_TRANSIT)
    .sort((a, b) => a.datetime.getTime() - b.datetime.getTime())[0]?.datetime;
  return {
    trackingCode: tracker?.trackingCode,
    trackingCarrier: toKnownCarrier(tracker?.carrier),
    trackingUrl: tracker
      ? getTrackingLink(
          getTrackingLinkCarrier(tracker.carrier),
          tracker.trackingCode,
          tracker.publicUrl,
          boughtRate?.service,
        )
      : null,
    trackingStatus: tracker?.status,
    estDeliveryDate: tracker?.estDeliveryDate,
    deliveredAt,
    firstScanAt,
  };
}

export function emptyShipmentSummaryBase(): ShipmentSummaryBase {
  return { carrierShipments: [] };
}

function buildCarrierShipmentSummary(
  shipment: ShipmentPackage,
): CarrierShipmentSummary {
  const { boughtRate, label } = shipment;
  return {
    purchased: !!boughtRate,
    carrier: boughtRate?.carrier,
    service: boughtRate?.service,
    purchasedAt: boughtRate?.created_at
      ? new Date(boughtRate.created_at)
      : null,
    provider: boughtRate?.provider,
    rateVersion: boughtRate?.version,
    simpleRateCode:
      boughtRate?.simple_rate_code ??
      boughtRate?.providerInfo?.ups?.simpleRate?.code,
    isMerchantCarrierAccount: boughtRate?.is_merchant_carrier_account,
    isExternalBilling: isExternalAccountBilling(boughtRate?.payment_option),
    billingAccountId: getBillingAccountId(boughtRate?.payment_option),
    cost: boughtRate
      ? {
          amount: Number(boughtRate.rate).toFixed(2),
          currency: toCurrency(boughtRate.currency),
        }
      : null,
    voided: !!shipment.voided,
    hasLabel: !!label?.url,
    commercialInvoiceUrl: label?.commercialInvoiceUrl,
    comInvoiceSubmittedElectronically: label?.comInvoiceSubmittedElectronically,
    createdAt: shipment.createdAt,
    carrierAdjustments: shipment.surcharges?.length
      ? shipment.surcharges
      : null,
    packages: [buildPackageSummary(shipment)],
  };
}

/** Legacy shipments carry no carrier-shipment grouping, so each maps to its
 * own single-package carrier shipment; V2 sources may group many packages
 * under one. */
export function buildShipmentSummaryBase(
  shipments: ShipmentPackage[],
): ShipmentSummaryBase | null {
  if (!shipments.length) {
    return null;
  }
  return {
    carrierShipments: shipments.map(buildCarrierShipmentSummary),
    totalCost: shipments.some((s) => s.boughtRate)
      ? {
          amount: shipments
            .reduce((acc, s) => acc + Number(s.boughtRate?.rate ?? 0), 0)
            .toFixed(2),
          currency: toCurrency(
            shipments.find((s) => s.boughtRate)?.boughtRate?.currency,
          ),
        }
      : null,
  };
}

export function summaryPackages(
  summary: ShipmentSummaryBase | null | undefined,
): ShipmentPackageSummary[] {
  return summary?.carrierShipments.flatMap((cs) => cs.packages) ?? [];
}

export function primaryCarrierShipment(
  summary: ShipmentSummaryBase | null | undefined,
): CarrierShipmentSummary | null {
  return summary?.carrierShipments[0] ?? null;
}

/** Cutover shim: prefers the server summary, else maps the deprecated fields. */
export function getEffectiveShipmentSummaryBase(data: {
  shipmentSummary?: ShipmentSummary | null;
  shipment?: ShipmentPackage | null;
  additionalShipments?: ShipmentPackage[] | null;
}): ShipmentSummaryBase | null {
  if (data.shipmentSummary) {
    return data.shipmentSummary;
  }
  return buildShipmentSummaryBase(
    [data.shipment, ...(data.additionalShipments ?? [])].filter(
      (s): s is ShipmentPackage => !!s,
    ),
  );
}
